/* eslint-disable @typescript-eslint/no-unused-vars */
import { Artist } from './artist';
import { NextInQueue } from './next';
import { NowPlayingCard } from './data';
import { Link } from 'react-router-dom';
import { NowPlayingLayout } from '../layout';
import { MenuIcon } from '../../../../Icons';
import { TrackActionsWrapper } from '../../../../Actions/TrackActions';
import { AddSongToLibraryButton } from '../../../../Actions/AddSongToLibrary';

// Redux
import { spotifyActions } from '../../../../../store/slices/spotify';
import { uiActions } from '../../../../../store/slices/ui';
import { CollapseRightIcon, ExpandIcon, MenuDots } from '../../../../Icons';
import { useAppDispatch, useAppSelector } from '../../../../../store/store';
import { Marquee } from '../../PlayingBar/Marquee';

import { memo, ReactNode, useEffect, useMemo, type FC } from 'react';

// Redux
import { fetchArtist } from '../../../../../store/slices/playingNow';

const Container: FC<{ song: Spotify.Track }> = memo(({ song }) => {
  const dispatch = useAppDispatch();
  const isLiked = useAppSelector((state) => state.spotify.liked);

  const handleToggle = () => {
    dispatch(spotifyActions.setLiked({ liked: !isLiked }));
  };

  return (
    <TrackActionsWrapper
      track={song}
      saved={isLiked}
      trigger={['contextMenu']}
      onSavedToggle={() => {
        dispatch(spotifyActions.setLiked({ liked: !isLiked }));
      }}
    >
      <div>
        <NowPlayingCard
          title={song.name}
          image={song.album.images[0].url}
          albumId={song.album.uri.split(':').reverse()[0]}
          subtitle={
            <span>
              {song.artists.slice(0, 3).map((a, i) => (
                <span key={a.uri}>
                  <Link to={`/artist/${a.uri.split(':').reverse()[0]}`}>{a.name}</Link>
                  {i < song.artists.slice(0, 3).length - 1 && ', '}
                </span>
              ))}
            </span>
          }
          searchQuery={`${song.name} ${song.artists[0]?.name || ''} official music video`}
          extra={<AddSongToLibraryButton id={song.id!} isSaved={isLiked} onToggle={handleToggle} />}
        />
      </div>
    </TrackActionsWrapper>
  );
});

const DetailsContainer: FC<{ children: ReactNode | ReactNode[] }> = memo((props) => {
  const dispatch = useAppDispatch();

  const isLiked = useAppSelector((state) => state.spotify.liked);
  const context = useAppSelector(
    (state) => state.spotify.state?.context,
    (a, b) => a?.uri === b?.uri
  );
  const song = useAppSelector(
    (state) => state.spotify.state?.track_window.current_track!,
    (a, b) => a?.id === b?.id
  );

  const contextDetails = useMemo(() => {
    const [_, type, id] = context?.uri?.split(':') || [];
    // @ts-ignore
    const name = context?.metadata?.context_description || song.name;
    if (type === 'playlist') {
      return { title: name, link: `/playlist/${id}` };
    } else if (type === 'album') {
      return { title: name, link: `/album/${id}` };
    } else if (type === 'artist') {
      return { title: name, link: `/artist/${id}` };
    } else {
      return { title: name, link: `/album/${song.album.uri.split(':')[2]}` };
    }
  }, [context, song]);

  return (
    <div className='playing-section'>
      <div className='playlist-section-content' style={{ height: '100%' }}>
        {props.children}
      </div>
    </div>
  );
});

export const Details = memo(({ collapsed }: { collapsed?: boolean }) => {
  const dispatch = useAppDispatch();

  const song = useAppSelector(
    (state) => state.spotify.state?.track_window.current_track,
    (a, b) => a?.id === b?.id
  );

  const artistId = useMemo(
    () => song?.artists?.[0]?.uri?.split(':')?.[2] || (song?.artists?.[0] as any)?.id,
    [song]
  );

  useEffect(() => {
    if (artistId && artistId !== '0') dispatch(fetchArtist(artistId));
  }, [artistId, dispatch]);

  const extra = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <button className='hoverable-icon' title="More options" style={{ transform: 'scale(0.8)' }}>
        <MenuDots />
      </button>
      <button 
        className='hoverable-icon' 
        onClick={() => dispatch(uiActions.toggleFullscreenNowPlaying())}
        title="Fullscreen"
        style={{ transform: 'scale(0.8)' }}
      >
        <ExpandIcon />
      </button>
    </div>
  );

  if (!song) return null;

  return (
    <div 
      className={collapsed ? "collapsed-details-wrapper" : "expanded-details-wrapper"}
      style={{ height: '100%', cursor: collapsed ? 'pointer' : 'default', position: 'relative' }}
      onClick={() => {
        if (collapsed) {
          dispatch(uiActions.toggleDetails());
        }
      }}
      title={collapsed ? "Expand Now Playing" : undefined}
    >
      <div className="collapsed-overlay">
        <div className="collapsed-overlay-icon">
          <svg role="img" height="16" width="16" aria-hidden="true" viewBox="0 0 16 16" fill="currentColor">
            <path d="M11.03.47a.75.75 0 0 1 0 1.06L4.56 8l6.47 6.47a.75.75 0 1 1-1.06 1.06L2.44 8 9.97.47a.75.75 0 0 1 1.06 0z"></path>
          </svg>
        </div>
      </div>
      <div style={{ pointerEvents: collapsed ? 'none' : 'auto', height: '100%' }}>
        <DetailsContainer>
          <div className="details-header-custom" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 8px 16px', width: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, marginRight: '12px' }}>
              <div className="collapse-button-wrapper" style={{ flexShrink: 0 }}>
                <button 
                  className='hoverable-icon details-collapse-button' 
                  onClick={() => dispatch(uiActions.collapseDetails())}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
                  title="Collapse"
                >
                  <CollapseRightIcon />
                </button>
              </div>
              {song && (
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Marquee title={song.name}>
                    <Link className='title-link' to={`/track/${song.id}`} style={{ fontWeight: 700, fontSize: '16px', color: '#fff', textDecoration: 'none' }}>
                      {song.name}
                    </Link>
                  </Marquee>
                </div>
              )}
            </div>
            {extra}
          </div>
          <div>
            <Container song={song} />
            <Artist />
            <NextInQueue />
          </div>
        </DetailsContainer>
      </div>
    </div>
  );
});
